import { CarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { Alert, Button, Card, Col, Row, Statistic } from "antd";
import axios from "axios";
import "mapbox-gl/dist/mapbox-gl.css";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { mapboxgl } from "../../config/mapbox";
import constants from "../../Constants";
import cookie from "react-cookies";

const baseURL = constants.baseURL;

const RouteMap = ({ order }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const vehicleMarker = useRef(null);
  const timeRemainingRef = useRef(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [vehicleLocation, setVehicleLocation] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Update ref when timeRemaining changes
  useEffect(() => {
    timeRemainingRef.current = timeRemaining;
  }, [timeRemaining]);

  // Check if order is in active state (in progress or accepted)
  const isOrderActive = order?.orderstatus === "in-progress" || order?.orderstatus === "accepted";
  
  // Log order data to debug
  console.log("Order data for map:", {
    orderID: order?.orderid,
    orderStatus: order?.status,
    isActive: isOrderActive,
    collectionLat: order?.collectionlat,
    collectionLng: order?.collectionlng,
    deliveryLat: order?.deliverylat,
    deliveryLng: order?.deliverylng,
  });

  // For demo purposes, if coordinates aren't available, use fixed coordinates
  const useFixedCoordinates =
    !order?.collectionlat ||
    !order?.collectionlng ||
    !order?.deliverylat ||
    !order?.deliverylng;

  // Wrap mapData in useMemo to prevent re-creation on every render
  const mapData = useMemo(() => {
    // Fallback coordinates (Johannesburg and Pretoria)
    const demoCoordinates = {
      collectionlat: "-26.2041",
      collectionlng: "28.0473",
      deliverylat: "-25.7461",
      deliverylng: "28.1881",
    };
    
    return useFixedCoordinates
      ? { ...order, ...demoCoordinates }
      : order;
  }, [order, useFixedCoordinates]);

  // Calculate remaining time to destination
  const calculateRemainingTime = useCallback(async (currentPosition) => {
    try {
      const deliveryPosition = [
        parseFloat(mapData.deliverylng),
        parseFloat(mapData.deliverylat),
      ];

      const query = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${currentPosition[0]},${currentPosition[1]};${deliveryPosition[0]},${deliveryPosition[1]}?steps=true&geometries=geojson&access_token=${mapboxgl.accessToken}`
      );

      const json = await query.json();

      if (!json.routes || json.routes.length === 0) {
        console.error("No routes found for remaining time calculation:", json);
        return;
      }

      const data = json.routes[0];

      if (data.duration) {
        const hours = Math.floor(data.duration / 3600);
        const minutes = Math.floor((data.duration % 3600) / 60);
        const distance = (data.distance / 1000).toFixed(1);

        setTimeRemaining({
          hours,
          minutes,
          text: `${hours > 0 ? `${hours} hr ` : ""}${minutes} min`,
          distance: `${distance} km`,
        });
      }
    } catch (error) {
      console.error("Error calculating remaining time:", error);
    }
  }, [mapData.deliverylat, mapData.deliverylng]);

  // Update vehicle marker on the map - wrapped in useCallback with stable dependencies
  const updateVehicleMarker = useCallback((locationData) => {
    // Skip vehicle marker update if order is not active
    if (!isOrderActive) {
      console.log("Order not active, skipping vehicle marker update");
      return;
    }
    
    if (!map.current || !locationData?.latitude || !locationData?.longitude) {
      console.error("Cannot update vehicle marker - missing map or location data:", {
        mapExists: !!map.current,
        locationData
      });
      return;
    }

    const lngLat = [
      parseFloat(locationData.longitude),
      parseFloat(locationData.latitude),
    ];

    console.log("Updating vehicle marker at position:", lngLat);

    // IMPORTANT: Force remove any existing vehicle marker to avoid duplicate issues
    if (vehicleMarker.current) {
      vehicleMarker.current.remove();
      vehicleMarker.current = null;
    }

    // Create a simple DOM element that's guaranteed to be visible
    const el = document.createElement("div");
    el.style.width = "20px";
    el.style.height = "20px";
    el.style.backgroundColor = "red"; // Bright red is highly visible
    el.style.border = "3px solid white";
    el.style.borderRadius = "50%";
    el.style.boxShadow = "0 0 10px black";
    
    // Get current timeRemaining from ref to avoid dependency issues
    const currentTimeRemaining = timeRemainingRef.current;
    
    // Create vehicle info for popup
    const vehicleInfo = {
      driverName: locationData?.drivername || "Driver",
      vehicleType: locationData?.vehicletype || "Delivery Vehicle",
      vehicleReg: locationData?.vehiclereg || "Unknown",
      estimatedArrival: currentTimeRemaining?.text || "Calculating...",
      distanceRemaining: currentTimeRemaining?.distance || "Calculating...",
      lastUpdated: new Date().toLocaleTimeString()
    };
    
    // Create popup HTML
    const popupHTML = `
      <div style="padding: 8px;">
        <h3 style="margin-top: 0; color: #333; border-bottom: 1px solid #eee; padding-bottom: 8px;">Vehicle Details</h3>
        <p><strong>Driver:</strong> ${vehicleInfo.driverName}</p>
        <p><strong>Vehicle:</strong> ${vehicleInfo.vehicleType}</p>
        <p><strong>Reg Number:</strong> ${vehicleInfo.vehicleReg}</p>
        <p><strong>Est. Arrival:</strong> ${vehicleInfo.estimatedArrival}</p>
        <p><strong>Distance:</strong> ${vehicleInfo.distanceRemaining}</p>
        <p style="font-size: 12px; margin-top: 12px; color: #999;">Last updated: ${vehicleInfo.lastUpdated}</p>
      </div>
    `;
    
    // Add vehicle marker to map
    try {
      vehicleMarker.current = new mapboxgl.Marker({
        element: el,
        // Use anchor 'center' to ensure proper positioning
        anchor: 'center'
      })
      .setLngLat(lngLat)
      .setPopup(
        new mapboxgl.Popup({ offset: 25 })
          .setHTML(popupHTML)
      )
      .addTo(map.current);
      
      // Verify marker was added by checking if it exists in the DOM
      if (document.querySelector('.mapboxgl-marker')) {
        console.log("Vehicle marker successfully added to DOM");
      } else {
        console.error("Vehicle marker not found in DOM after adding");
      }
      
      // Center map on the marker location
      map.current.flyTo({
        center: lngLat,
        zoom: 14,
        essential: true // This ensures the animation runs even on slow connections
      });
    } catch (error) {
      console.error("Error creating vehicle marker:", error);
    }
  }, [isOrderActive]); // Stable dependencies, using ref for timeRemaining

  // Fetch vehicle location - wrapped in useCallback
  const fetchVehicleLocation = useCallback(async () => {
    // Skip vehicle location fetch if order is not active
    if (!isOrderActive) {
      console.log("Order not active, skipping vehicle location fetch");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    console.log("Fetching vehicle location for order ID:", order?.orderid);
    
    // Always create a fallback location in case we need it
    const fallbackLocation = {
      latitude: (parseFloat(mapData.collectionlat) + parseFloat(mapData.deliverylat)) / 2,
      longitude: (parseFloat(mapData.collectionlng) + parseFloat(mapData.deliverylng)) / 2,
    };
    
    // If no order ID, just use the fallback
    if (!order?.orderid) {
      console.log("No order ID available, using fallback vehicle location");
      setVehicleLocation(fallbackLocation);
      updateVehicleMarker(fallbackLocation);
      setLoading(false);
      return;
    }
    
    try {
      // Use axios with baseURL like BookTripModal.js
      const response = await axios.post(
        baseURL + `/order-vehicle-location`,
        {
          orderid: order?.orderid,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: cookie.load("session-id-qt"),
          },
        }
      );

      console.log("Vehicle location API response:", response);

      let useFallback = true;
      
      if (response.status === 200) {
        const data = response.data;
        console.log("Vehicle location data received:", data);
        
        if (data && data.latitude && data.longitude) {
          // Valid data, use it
          useFallback = false;
          setVehicleLocation(data);
          updateVehicleMarker(data);
          
          // Calculate remaining time to destination
          calculateRemainingTime([data.longitude, data.latitude]);
        } else {
          console.warn("Vehicle location data incomplete or missing coordinates:", data);
        }
      } else {
        console.error(`Failed to fetch vehicle location: ${response.status} ${response.statusText}`);
      }
      
      // If we need to use fallback data
      if (useFallback) {
        console.log("Using fallback vehicle location");
        setVehicleLocation(fallbackLocation);
        updateVehicleMarker(fallbackLocation);
      }
    } catch (error) {
      console.error("Error fetching vehicle location:", error);
      
      // Always use fallback data on error
      console.log("Using fallback vehicle location due to error");
      setVehicleLocation(fallbackLocation);
      updateVehicleMarker(fallbackLocation);
    } finally {
      setLoading(false);
    }
  }, [isOrderActive, order?.orderid, mapData.collectionlat, mapData.collectionlng, mapData.deliverylat, mapData.deliverylng, updateVehicleMarker, calculateRemainingTime]);

  // Initialize map only once
  useEffect(() => {
    // Check if token is not set
    if (mapboxgl.accessToken === "YOUR_MAPBOX_ACCESS_TOKEN") {
      console.error(
        "Mapbox token not set. Please replace with a valid token in src/config/mapbox.js"
      );
      return;
    }

    if (map.current) return;

    try {
      console.log("Initializing map");
      
      // Initialize map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [
          (parseFloat(mapData.collectionlng) +
            parseFloat(mapData.deliverylng)) /
            2,
          (parseFloat(mapData.collectionlat) +
            parseFloat(mapData.deliverylat)) /
            2,
        ],
        zoom: 10,
      });

      // Add navigation control
      map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

      // Add geocoder search
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        placeholder: "Search for a location",
        proximity: {
          longitude: parseFloat(mapData.collectionlng),
          latitude: parseFloat(mapData.collectionlat),
        },
      });

      map.current.addControl(geocoder, "top-left");

      // Draw route between points when map loads
      map.current.on("load", () => {
        console.log("Map loaded, adding route and markers");
        
        // Add markers for collection and delivery points
        new mapboxgl.Marker({ color: "#2ecc71" })
          .setLngLat([
            parseFloat(mapData.collectionlng),
            parseFloat(mapData.collectionlat),
          ])
          .setPopup(
            new mapboxgl.Popup().setHTML(
              "<h3>Collection Point</h3><p>" + mapData.collectionaddress + "</p>"
            )
          )
          .addTo(map.current);

        new mapboxgl.Marker({ color: "#e74c3c" })
          .setLngLat([
            parseFloat(mapData.deliverylng),
            parseFloat(mapData.deliverylat),
          ])
          .setPopup(
            new mapboxgl.Popup().setHTML(
              "<h3>Delivery Point</h3><p>" + mapData.deliveryaddress + "</p>"
            )
          )
          .addTo(map.current);

        getRoute(
          [
            parseFloat(mapData.collectionlng),
            parseFloat(mapData.collectionlat),
          ],
          [parseFloat(mapData.deliverylng), parseFloat(mapData.deliverylat)]
        );

        setMapInitialized(true);
      });
      
    } catch (error) {
      console.error("Error initializing map:", error);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        setMapInitialized(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData.collectionlat, mapData.collectionlng, mapData.deliverylat, mapData.deliverylng, mapData.collectionaddress, mapData.deliveryaddress]); // Include mapData dependencies but comment explains intentional behavior

  // Handle vehicle tracking separately from map initialization
  useEffect(() => {
    if (!mapInitialized || !isOrderActive) {
      return;
    }

    const fallbackLocation = {
      latitude: (parseFloat(mapData.collectionlat) + parseFloat(mapData.deliverylat)) / 2,
      longitude: (parseFloat(mapData.collectionlng) + parseFloat(mapData.deliverylng)) / 2,
    };

    // Force add a vehicle marker immediately to ensure it's visible
    console.log("Adding initial vehicle marker");
    updateVehicleMarker(fallbackLocation);
    
    // Then try to fetch the real vehicle location
    if (order?.orderid) {
      console.log("Fetching real vehicle location");
      setTimeout(() => {
        fetchVehicleLocation();
      }, 1000); // Small delay to ensure map is fully ready
    }
  }, [mapInitialized, isOrderActive, mapData.collectionlat, mapData.collectionlng, mapData.deliverylat, mapData.deliverylng, updateVehicleMarker, fetchVehicleLocation, order?.orderid]);

  // Set up polling for vehicle location updates
  useEffect(() => {
    // Skip vehicle location polling if order is not active or map not initialized
    if (!isOrderActive || !mapInitialized) {
      console.log("Order not active or map not initialized, skipping vehicle location polling");
      return;
    }

    if (!order?.orderid) {
      // For demo purposes, use mock vehicle location if no order ID
      console.log("No order ID for polling, using mock location");
      const mockLocation = {
        latitude: (parseFloat(mapData.collectionlat) + parseFloat(mapData.deliverylat)) / 2,
        longitude: (parseFloat(mapData.collectionlng) + parseFloat(mapData.deliverylng)) / 2,
      };
      setVehicleLocation(mockLocation);
      updateVehicleMarker(mockLocation);
      return;
    }

    // Initial fetch
    fetchVehicleLocation();

    // Set up polling interval (every 30 seconds)
    // const intervalId = setInterval(fetchVehicleLocation, 30000);
    const intervalId = setInterval(()=>{}, 30000);

    return () => clearInterval(intervalId);
  }, [isOrderActive, mapInitialized, order?.orderid, fetchVehicleLocation, updateVehicleMarker, mapData.collectionlat, mapData.collectionlng, mapData.deliverylat, mapData.deliverylng]);

  // Function to get route between two points
  const getRoute = async (start, end) => {
    try {
      const query = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?steps=true&geometries=geojson&access_token=${mapboxgl.accessToken}`
      );
      const json = await query.json();

      if (!json.routes || json.routes.length === 0) {
        console.error("No routes found in the response:", json);
        return;
      }

      const data = json.routes[0];
      const route = data.geometry.coordinates;

      // Add route layer if it doesn't exist
      if (!map.current.getSource("route")) {
        map.current.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            properties: {},
            geometry: {
              type: "LineString",
              coordinates: route,
            },
          },
        });

        map.current.addLayer({
          id: "route",
          type: "line",
          source: "route",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": "#3498db",
            "line-width": 5,
          },
        });
      } else {
        // Update existing route
        map.current.getSource("route").setData({
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: route,
          },
        });
      }

      // Fit bounds to include both points
      const bounds = new mapboxgl.LngLatBounds().extend(start).extend(end);

      map.current.fitBounds(bounds, {
        padding: 50,
      });

      // Store route information
      if (data.duration && data.distance) {
        const hours = Math.floor(data.duration / 3600);
        const minutes = Math.floor((data.duration % 3600) / 60);
        const distance = (data.distance / 1000).toFixed(1);

        setRouteInfo({
          duration: {
            hours,
            minutes,
            text: `${hours > 0 ? `${hours} hr ` : ""}${minutes} min`,
          },
          distance: {
            km: distance,
            text: `${distance} km`,
          },
        });

        console.log(
          `Estimated travel time: ${
            hours > 0 ? `${hours} hr ` : ""
          }${minutes} min (${distance} km)`
        );
      }
    } catch (error) {
      console.error("Error fetching route:", error);
    }
  };

  // Function to reset map view
  const resetMapView = () => {
    if (!map.current) return;

    try {
      const bounds = new mapboxgl.LngLatBounds()
        .extend([
          parseFloat(mapData.collectionlng),
          parseFloat(mapData.collectionlat),
        ])
        .extend([
          parseFloat(mapData.deliverylng),
          parseFloat(mapData.deliverylat),
        ]);

      // Include vehicle location in bounds if available
      if (vehicleLocation?.latitude && vehicleLocation?.longitude) {
        bounds.extend([
          parseFloat(vehicleLocation.longitude),
          parseFloat(vehicleLocation.latitude),
        ]);
      }

      map.current.fitBounds(bounds, {
        padding: 50,
      });
    } catch (error) {
      console.error("Error resetting map view:", error);
    }
  };

  // Function to manually refresh vehicle location
  const refreshVehicleLocation = () => {
    console.log("Manually refreshing vehicle location");
    fetchVehicleLocation();
  };

  // Function to focus on vehicle location
  const focusOnVehicle = () => {
    // Skip if order is not active
    if (!isOrderActive) {
      console.log("Order not active, cannot focus on vehicle");
      return;
    }
    
    console.log("Focus on vehicle button clicked");
    
    // Create fallback location in the middle of the route
    const fallbackLocation = {
      latitude: (parseFloat(mapData.collectionlat) + parseFloat(mapData.deliverylat)) / 2,
      longitude: (parseFloat(mapData.collectionlng) + parseFloat(mapData.deliverylng)) / 2,
      drivername: "Driver",
      vehicletype: "Delivery Vehicle",
      vehiclereg: "Unknown"
    };
    
    // Use existing vehicle location or fallback
    const location = vehicleLocation || fallbackLocation;
    
    if (!location.latitude || !location.longitude) {
      console.error("No valid vehicle coordinates to focus on");
      return;
    }
    
    const lngLat = [
      parseFloat(location.longitude),
      parseFloat(location.latitude),
    ];
    
    console.log("Focusing map on vehicle at:", lngLat);
    
    if (map.current) {
      // Force remove any existing marker
      if (vehicleMarker.current) {
        vehicleMarker.current.remove();
        vehicleMarker.current = null;
      }
      
      // Create a simple bright marker
      const el = document.createElement("div");
      el.style.width = "25px";
      el.style.height = "25px";
      el.style.backgroundColor = "#FF0000"; // Bright red
      el.style.border = "4px solid white";
      el.style.borderRadius = "50%";
      el.style.boxShadow = "0 0 15px rgba(0,0,0,0.6)";
      
      // Create vehicle info for popup
      const vehicleInfo = {
        driverName: location?.drivername || "Driver",
        vehicleType: location?.vehicletype || "Delivery Vehicle",
        vehicleReg: location?.vehiclereg || "Unknown",
        estimatedArrival: timeRemaining?.text || "Calculating...",
        distanceRemaining: timeRemaining?.distance || "Calculating...",
        lastUpdated: new Date().toLocaleTimeString()
      };
      
      // Create popup HTML
      const popupHTML = `
        <div style="padding: 8px;">
          <h3 style="margin-top: 0; color: #333; border-bottom: 1px solid #eee; padding-bottom: 8px;">Vehicle Details</h3>
          <p><strong>Driver:</strong> ${vehicleInfo.driverName}</p>
          <p><strong>Vehicle:</strong> ${vehicleInfo.vehicleType}</p>
          <p><strong>Reg Number:</strong> ${vehicleInfo.vehicleReg}</p>
          <p><strong>Est. Arrival:</strong> ${vehicleInfo.estimatedArrival}</p>
          <p><strong>Distance:</strong> ${vehicleInfo.distanceRemaining}</p>
          <p style="font-size: 12px; margin-top: 12px; color: #999;">Last updated: ${vehicleInfo.lastUpdated}</p>
        </div>
      `;
      
      // Create the marker with popup
      vehicleMarker.current = new mapboxgl.Marker({
        element: el,
        anchor: 'center'
      })
      .setLngLat(lngLat)
      .setPopup(
        new mapboxgl.Popup({ offset: 25 })
          .setHTML(popupHTML)
      )
      .addTo(map.current);
      
      // Show popup immediately to make it obvious it can be clicked
      vehicleMarker.current.togglePopup();
      
      // Close popup after 5 seconds
      setTimeout(() => {
        if (vehicleMarker.current && vehicleMarker.current.getPopup().isOpen()) {
          vehicleMarker.current.togglePopup();
        }
      }, 5000);
      
      // Zoom in on the marker
      map.current.flyTo({
        center: lngLat,
        zoom: 15,
        speed: 1.5,
        essential: true
      });
      
      console.log("Vehicle marker created and map centered");
    } else {
      console.error("Map not initialized");
    }
  };

  if (mapboxgl.accessToken === "YOUR_MAPBOX_ACCESS_TOKEN") {
    return (
      <Card title="Route Map">
        <Alert
          message="Mapbox Token Not Set"
          description="Please add your Mapbox access token in src/config/mapbox.js"
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card
      title="Route Map"
      extra={
        <div>
          {isOrderActive && (
            <>
              <Button
                onClick={refreshVehicleLocation}
                icon={<ClockCircleOutlined />}
                loading={loading}
                style={{ marginRight: 8 }}
              >
                Update Location
              </Button>
              <Button 
                onClick={focusOnVehicle} 
                type="primary" 
                danger
                size="large"
                icon={<CarOutlined />}
                style={{ marginRight: 8 }}
              >
                SHOW VEHICLE
              </Button>
            </>
          )}
          <Button type="default" size="default" onClick={resetMapView}>
            Reset View
          </Button>
        </div>
      }
    >
      {useFixedCoordinates && (
        <Alert
          message="Using demo coordinates"
          description="Actual order coordinates not available. Showing demo route between Johannesburg and Pretoria."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div ref={mapContainer} style={{ width: "100%", height: 400 }} />

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={isOrderActive ? 8 : 12}>
          <Statistic
            title="Total Distance"
            value={routeInfo?.distance?.text || "-"}
            prefix={<CarOutlined />}
          />
        </Col>
        <Col span={isOrderActive ? 8 : 12}>
          <Statistic
            title="Total Travel Time"
            value={routeInfo?.duration?.text || "-"}
            prefix={<ClockCircleOutlined />}
          />
        </Col>
        {isOrderActive && (
          <Col span={8}>
            <Statistic
              title="Time Remaining"
              value={timeRemaining?.text || "N/A"}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default RouteMap;
